import { useState, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from '@softland/forms';
import { yupResolver } from '@softland/forms/resolver';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  FormControl,
  useTheme,
  Stack,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';

import { credentials } from 'modules/auth/api/auth.api';
import { loginValidation } from './validation';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

interface LoginForm {
  email: string;
  password: string;
}
const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    mode: 'onBlur',
    resolver: yupResolver(loginValidation),
  });

  const onSubmit: SubmitHandler<LoginForm> = async data => {
    setLoading(true);
    setErrorMessage('');
    const queryParams = new URLSearchParams(location.search);

    const fp = await FingerprintJS.load();
    const result = await fp.get();

    try {
      const response = await credentials({
        username: data.email,
        password: data.password,
        returnurl: queryParams.get('ReturnUrl') || '',
        deviceFingerprint: result.visitorId,
      });

      const returnUrl = queryParams.get('ReturnUrl') || '';

      if (returnUrl) {
        window.location.replace(returnUrl);
      } else {
        window.location.replace(response.data);
      }
    } catch (error: any) {
      switch (error.response.status) {
        case 401:
          if (error.response.data.message === 'User is Blocked') {
            setErrorMessage(error.response.data.message);
          } else {
            setErrorMessage('You have entered an invalid password');
          }
          break;
        case 404:
          setErrorMessage('You have entered an invalid username or password');
          break;
        case 451:
          if (error.response.data.twoFaInfo) {
            navigate('/two-fa', {
              state: {
                twoFaState: error.response.data,
                returnUrl: queryParams.get('ReturnUrl') || '',
              },
            });
          } else {
            navigate('/otp', {
              state: {
                twoFaState: error.response.data,
                returnUrl: queryParams.get('ReturnUrl') || '',
              },
            });
          }

          break;
        default:
          console.log(error.message);
      }
    }

    setLoading(false);
  };

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  return (
    <Box
      sx={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.09)' : 'none',
      }}
    >
      <Box>
        <Typography variant="h4" className="qa-login-title" fontWeight={600}>
          Welcome Back!
        </Typography>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ color: theme.palette.text.secondary, marginTop: '7px', marginBottom: '15px' }}
          className="qa-welcome-message"
        >
          Please enter your details
        </Typography>
      </Box>
      <Box width="100%">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            <TextField
              size="medium"
              id="qa-login-email"
              label="Email"
              variant="outlined"
              tabIndex={1}
              error={!!errors.email}
              {...register('email')}
            />

            <FormControl sx={{ width: '100%' }} variant="outlined" error={!!errors.password}>
              <InputLabel htmlFor="qa-login-password">Password</InputLabel>
              <OutlinedInput
                {...register('password')}
                size="medium"
                id="qa-login-password"
                tabIndex={2}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      className="qa-login-password-visibility-btn"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff className="qa-visibility-off-icon" />
                      ) : (
                        <Visibility className="qa-visibility-on-icon" />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
              />
            </FormControl>
            <Box>
              <LoadingButton
                id="qa-login-button"
                loading={loading}
                size="large"
                variant="contained"
                type="submit"
                fullWidth
              >
                login
              </LoadingButton>
              {errorMessage && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.error.main, padding: '10px' }}
                  className="qa-error-message"
                >
                  {errorMessage}
                </Typography>
              )}
            </Box>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default Login;
